// Utilities for converting monthly task templates into concrete task date ranges.
//
// Contract:
// - month is 1..12
// - year is full year (e.g. 2026)
// - dayOfMonth is integer in -31..-1 or 1..31 (no 0)
// - duration is integer >= 0
// - returned values are UTC midnight timestamps (millis)

const DAY_MS = 24 * 60 * 60 * 1000;

function assertInt(name, v) {
  if (!Number.isFinite(v) || !Number.isInteger(v)) {
    throw new Error(`${name} must be an integer`);
  }
}

export function calcValidFromUtcMillis({ year, month, dayOfMonth }) {
  assertInt('year', year);
  assertInt('month', month);
  assertInt('dayOfMonth', dayOfMonth);

  if (month < 1 || month > 12) throw new Error('month must be 1..12');
  if (dayOfMonth === 0) throw new Error('dayOfMonth cannot be 0');

  // We compute using UTC to avoid timezone DST shifts.
  // Positive dayOfMonth: straightforward.
  if (dayOfMonth > 0) {
    // JS Date month is 0-based.
    return Date.UTC(year, month - 1, dayOfMonth, 0, 0, 0, 0);
  }

  // Negative dayOfMonth counts backwards from next month's 1st day.
  // Example: -1 => last day of selected month
  const nextMonthFirst = Date.UTC(year, month, 1, 0, 0, 0, 0); // month is 1..12, so this is ok for Date.UTC
  return nextMonthFirst + dayOfMonth * DAY_MS; // dayOfMonth is negative
}

export function calcValidToUtcMillis({ validFromUtcMillis, duration }) {
  assertInt('duration', duration);
  if (duration < 0) throw new Error('duration cannot be negative');
  return validFromUtcMillis + duration * DAY_MS;
}

export function calcRangeUtcMillis({ year, month, dayOfMonth, duration }) {
  const validFrom = calcValidFromUtcMillis({ year, month, dayOfMonth });
  const validTo = calcValidToUtcMillis({ validFromUtcMillis: validFrom, duration });
  return { validFrom, validTo };
}
